/**
 * Qinggong time trials (data/trials.ts, docs/systems/FUN_P1.md section 3). `trialStart` on the start stone begins a run: the
 * rings are taken in order, the clock is the World's own step time, and the last ring ends it with a medal.
 * `trialQuit`, dying or being jailed ends it. Best times and the medals paid save in `progress.trials`; the first clear
 * of each medal pays once (`TRIAL_REWARDS`; a higher medal also pays the lower ones not yet taken).
 * Progress is told with notices (an event type for the HUD comes with the client's trial badge).
 */
import { medalFor, TRIAL, TRIAL_REWARDS, trialById, type TrialDef, type TrialMedal } from '../data/trials';
import { floorAt, PX_METRES } from '../map';
import { toCore } from '../region/core';
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`). */
export type TrialCommand = { t: 'trialStart'; id: string } | { t: 'trialQuit' };

/** Event (part of `WorldEvent`): a run's start, each ring, its end (`done` with the medal, or `fail` / `quit`). */
export type TrialEvent = {
  type: 'trial';
  state: 'start' | 'ring' | 'done' | 'fail' | 'quit';
  id: string;
  /** Rings taken so far (the start stone counts). */
  ring: number;
  t: number;
  medal: TrialMedal | null;
};

/** What the save keeps: the best time and the medals already paid, per trial. */
export interface TrialSave {
  readonly best: number;
  readonly medals: readonly TrialMedal[];
}
export type TrialsSave = Readonly<Record<string, TrialSave>>;

const MEDALS: readonly TrialMedal[] = ['bronze', 'silver', 'gold'];
const MEDAL_NAME: Readonly<Record<TrialMedal, string>> = { bronze: 'เหรียญทองแดง', silver: 'เหรียญเงิน', gold: 'เหรียญทอง' };

/** A save read back: unknown trials and bad values dropped. */
export function parseTrials(raw: unknown): TrialsSave {
  const out: Record<string, TrialSave> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    const r = v as { best?: unknown; medals?: unknown } | null;
    if (!trialById(id) || !r || typeof r.best !== 'number' || !Number.isFinite(r.best) || r.best <= 0) continue;
    const medals = Array.isArray(r.medals) ? MEDALS.filter((m) => (r.medals as unknown[]).includes(m)) : [];
    out[id] = { best: r.best, medals };
  }
  return out;
}

interface Run {
  readonly def: TrialDef;
  /** The next ring to take (0 is the start stone, taken by starting). */
  next: number;
  t: number;
}

export class TrialsSystem implements WorldSystem {
  readonly name = 'trials';
  private run: Run | null = null;

  constructor(private readonly h: SystemHost) {}

  /** The run under way: its trial's id, the next ring and the seconds so far, or null. */
  current(): { id: string; next: number; t: number } | null {
    return this.run ? { id: this.run.def.id, next: this.run.next, t: this.run.t } : null;
  }

  readonly commands = {
    trialStart: (cmd: Extract<TrialCommand, { t: 'trialStart' }>) => {
      const w = this.h.world;
      const def = trialById(cmd.id);
      const p = w.player;
      if (!def || def.zone !== w.map.zone || p.dead || p.jailT > 0 || p.mount) return;
      const s = def.rings[0]!;
      // The rings are in the town's own metres (a region's core is offset).
      const at = toCore(w.map, p.x, p.y);
      if (Math.hypot(at.x - s.x, at.y - s.y) > TRIAL.startReach) return void this.h.notice(`ไปที่หินเริ่มของ ${def.name} ก่อน`);
      const lack = Object.entries(def.needs).find(([id, lv]) => (w.progress.qinggong[id] ?? 0) < lv);
      if (lack) return void this.h.notice(`${def.name} ต้องมีวิชาตัวเบาครบก่อน`);
      this.run = { def, next: 1, t: 0 };
      this.send('start');
      this.h.notice(`เริ่ม ${def.name} · ${def.rings.length - 1} ด่าน`);
    },
    trialQuit: () => {
      if (!this.run) return;
      this.h.notice(`เลิก ${this.run.def.name}`);
      this.send('quit');
      this.run = null;
    },
  };

  step(dt: number): void {
    const r = this.run;
    if (!r) return;
    const w = this.h.world;
    const p = w.player;
    if (p.dead || p.jailT > 0 || p.mount || w.map.zone !== r.def.zone) {
      this.h.notice(`${r.def.name} ล้มเหลว`);
      this.send('fail');
      this.run = null;
      return;
    }
    r.t += dt;
    const ring = r.def.rings[r.next]!;
    const at = toCore(w.map, p.x, p.y);
    if (Math.hypot(at.x - ring.x, at.y - ring.y) > TRIAL.radius) return;
    if (ring.h !== undefined && Math.abs((floorAt(w.map, p.x, p.y) + p.z) * PX_METRES - ring.h) > TRIAL.band) return;
    r.next++;
    if (r.next < r.def.rings.length) {
      this.h.notice(`ผ่านด่าน ${r.next - 1}/${r.def.rings.length - 1} · ${r.t.toFixed(1)} วินาที`);
      this.send('ring');
      return;
    }
    this.finish(r);
    this.run = null;
  }

  private send(state: TrialEvent['state'], medal: TrialMedal | null = null): void {
    const r = this.run;
    if (r) this.h.emit({ type: 'trial', state, id: r.def.id, ring: r.next, t: Math.round(r.t * 10) / 10, medal });
  }

  private finish(r: Run): void {
    const w = this.h.world;
    const seconds = Math.round(r.t * 10) / 10;
    const medal = medalFor(r.def, seconds);
    const save = w.progress.trials?.[r.def.id];
    const had = new Set<TrialMedal>(save?.medals ?? []);
    const paid: string[] = [];
    if (medal) {
      for (const m of MEDALS.slice(0, MEDALS.indexOf(medal) + 1)) {
        if (had.has(m)) continue;
        had.add(m);
        paid.push(this.h.grantReward(TRIAL_REWARDS[m]));
      }
    }
    w.progress.trials = {
      ...w.progress.trials,
      [r.def.id]: { best: Math.min(save?.best ?? Infinity, seconds), medals: MEDALS.filter((m) => had.has(m)) },
    };
    this.send('done', medal);
    this.h.notice(`${r.def.name} จบที่ ${seconds} วินาที${medal ? ` · ${MEDAL_NAME[medal]}` : ' · ไม่ถึงเกณฑ์'}${paid.length ? ` · ${paid.join(' ')}` : ''}`);
  }
}
